import React, { useEffect, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import axios from 'axios';
import {
  Badge, Box, Divider, HStack, SimpleGrid, Spinner, Text, Textarea, useColorModeValue,
} from '@chakra-ui/react';
import { RnwyEngineResult, RnwyEngineOption, parseVenues } from './rnwyVenues';
import { money } from './rnwyCfg';
import RnwySubscriptionEnhancementsNote from '../RnwySubscriptionEnhancementsNote'; // item 1546
import {
  parsePricing, emptyPricing, RnwyOptionKey,
} from './rnwyPricing';

// RNWY 2.0 · P4 — live pricing preview. Debounces the venue JSON and posts it to the backend engine
// (single source of truth — no client-side re-implementation), rendering the three computed options.
// Read-only mirror of what the client will see + what prints on the PDF.

// item 629 — `crestron` renders the Crestron one-time quote (CCP + CCC) instead of the three
// RNWY licensing options, off the SAME venue inputs (the engine returns both).
const RnwyOptionsPreview: React.FC<{ crestron?: boolean }> = ({ crestron = false }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const venuesJson = (values as { rnwyVenuesJson?: string | null }).rnwyVenuesJson ?? '';
  // item 652 — the numbers are engine-driven (read-only), but each option keeps an editable FAQ /
  // notes blurb that prints on the PDF. It lives in rnwyPricingJson (item 656 seeds it from the
  // Settings → RNWY defaults). Numbers no longer come from here — the manual builder is retired.
  const pricingJson = (values as { rnwyPricingJson?: string | null }).rnwyPricingJson ?? undefined;
  const pricing = parsePricing(pricingJson) ?? emptyPricing();
  const setFaq = (key: RnwyOptionKey, faq: string) => {
    form.change('rnwyPricingJson', JSON.stringify({ ...pricing, [key]: { ...pricing[key], faq } }));
  };
  // item 637 — preview against THIS proposal's CFG snapshot so it matches what saves + prints
  // (blank on a brand-new quote → the backend uses the live global default).
  const cfgJson = (values as { rnwyCfgJson?: string | null }).rnwyCfgJson ?? '';
  const [result, setResult] = useState<RnwyEngineResult | null>(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // item 1019 (Chad/Tom) — the 600ms debounce made the priced options the LAST thing to appear on
  // an already-slow open. First compute fires immediately; the debounce only paces live edits.
  const firstRun = useRef(true);

  const cardBg = useColorModeValue('white', 'whiteAlpha.100');
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const sub = useColorModeValue('gray.500', 'whiteAlpha.600');
  const strong = useColorModeValue('gray.800', 'whiteAlpha.900');
  // item 680 (Chad) — the "SwitchPilot excluded" flag: a cool, colored callout (RNWY purple)
  const flagBg = useColorModeValue('purple.50', 'purple.900');
  const flagAccent = useColorModeValue('purple.500', 'purple.300');
  const flagText = useColorModeValue('purple.700', 'purple.100');

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const delay = firstRun.current ? 0 : 600;
    firstRun.current = false;
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_BASIX_API || '';
        const { data } = await axios.post(`${apiUrl}proposal/rnwy-preview`, { venuesJson, cfgJson });
        setResult(data as RnwyEngineResult);
      } catch {
        setResult(null);
      } finally {
        setLoading(false);
      }
    }, delay);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [venuesJson, cfgJson]);

  const hasSwitchPilot = !!result?.switchPilot?.hasSwitchPilot;
  if (!result || (result.venueCount === 0 && (crestron || !hasSwitchPilot))) {
    return (
      <Text fontSize="xs" color={sub}>
        Add venues above to see the
        {' '}
        {crestron ? 'Crestron price' : 'three pricing options'}
        {' '}
        compute here.
        {loading && <Spinner size="xs" ml={2} />}
      </Text>
    );
  }

  const row = (label: string, value: string, bold = false) => (
    <HStack justify="space-between">
      <Text fontSize="xs" color={bold ? strong : sub} fontWeight={bold ? 'semibold' : undefined}>{label}</Text>
      <Text fontSize="xs" color={strong} fontWeight={bold ? 'bold' : undefined}>{value}</Text>
    </HStack>
  );

  // item 675 — itemize the one-time add-ons (the catalog items added) instead of a "Special
  // services" lump, matching the PDF. Same list is included in every option's one-time.
  const oneTimeItems = parseVenues(venuesJson).jobLevel.misc ?? [];
  const oneTimeRows = oneTimeItems.map((m, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <HStack key={`ot-${i}`} justify="space-between">
      <Text fontSize="xs" color={sub} isTruncated maxW="70%">
        {`${(m.quantity ?? 1) > 1 ? `${m.quantity} × ` : ''}${m.sku ? `${m.label || 'Item'} · ${m.sku}` : (m.label || 'One-time item')}`}
      </Text>
      <Text fontSize="xs" color={strong}>{money(m.amount ?? 0)}</Text>
    </HStack>
  ));

  // item 680 (Chad) — itemize the SwitchPilot catalog add-ons (so you see where its config money
  // comes from), shown under the SwitchPilot config on the card + the 2yr/5yr options.
  const spExtras = (parseVenues(venuesJson).jobLevel.switchPilot?.specialItems ?? []).filter((it) => (it.amount ?? 0) > 0);
  const spExtraRows = spExtras.map((it, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <HStack key={`spx-${i}`} justify="space-between" pl={3}>
      <Text fontSize="xs" color={sub} isTruncated maxW="70%">
        {`${(it.quantity ?? 1) > 1 ? `${it.quantity} × ` : ''}${it.sku ? `${it.label || 'Item'} · ${it.sku}` : (it.label || 'Item')}`}
      </Text>
      <Text fontSize="xs" color={strong}>{money(it.amount ?? 0)}</Text>
    </HStack>
  ));

  // item 629 — Crestron one-time quote (reads as a standard quote): Certified Programming +
  // Certified Commissioning + hardware/graphics/dev, off the same venue inputs. No subscription.
  if (crestron) {
    const c = result.crestron;
    const deposit = Math.round((c.oneTimeSubtotal / 2) * 100) / 100;
    return (
      <Box>
        <HStack mb={2}>
          {/* HARD RULE (Chad): the word "Crestron" NEVER appears on a client-facing quote —
              this priced view mirrors the quote, so it stays brand-neutral. The internal form
              section heading tells the salesperson which model they're building. */}
          <Text fontWeight="semibold" fontSize="sm">Programming price (computed)</Text>
          <Badge colorScheme="purple">{`${result.venueCount} venue${result.venueCount === 1 ? '' : 's'}`}</Badge>
          {loading && <Spinner size="xs" />}
        </HStack>
        <Box borderWidth="1px" borderColor={border} borderRadius="md" bg={cardBg} p={3} maxW="md">
          <Text fontWeight="bold" fontSize="sm">Programming — one-time</Text>
          <Text fontSize="xx-small" color={sub} mb={2}>Standard quote · no subscription.</Text>
          {c.config > 0 && row('Configuration', money(c.config))}
          {c.graphics > 0 && row('Graphical illustrations', money(c.graphics))}
          {oneTimeRows}
          {row('Certified Programming', money(c.certifiedProgramming))}
          {row('Certified Commissioning', money(c.certifiedCommissioning))}
          <Divider my={1} />
          {row('One-time total', money(c.oneTimeSubtotal), true)}
          {c.oneTimeSubtotal > 0 && (
            <Text fontSize="xx-small" color={sub}>{`50/50: ${money(deposit)} down · ${money(c.oneTimeSubtotal - deposit)} on completion`}</Text>
          )}
        </Box>
      </Box>
    );
  }

  const card = (key: RnwyOptionKey, title: string, blurb: string, opt: RnwyEngineOption, isPerpetual: boolean) => {
    // SwitchPilot rides the SUBSCRIPTIONS only (never Perpetual): fold its one-time config into the
    // one-time total + add its monthly below the RNWY license as a combined total.
    // item 774 (Chad) — SwitchPilot is available on EVERY option now, Perpetual included: it
    // stays a subscription add-on (bills monthly + flags the deal for enrollment), and its
    // one-time configuration rides the option's 50/50 total automatically.
    const spOn = hasSwitchPilot;
    const spCfg = spOn ? (result.switchPilot?.oneTimeConfig ?? 0) : 0;
    const spMo = spOn ? (result.switchPilot?.monthly ?? 0) : 0;
    const oneTimeTotal = opt.oneTimeSubtotal + spCfg;
    const deposit = Math.round((oneTimeTotal / 2) * 100) / 100;
    return (
      <Box borderWidth="1px" borderColor={border} borderRadius="md" bg={cardBg} p={3}>
        <Text fontWeight="bold" fontSize="sm">{title}</Text>
        <Text fontSize="xx-small" color={sub} mb={2}>{blurb}</Text>
        {opt.config > 0 && row('Configuration', money(opt.config))}
        {opt.graphics > 0 && row('Graphical illustrations', money(opt.graphics))}
        {oneTimeRows}
        {isPerpetual
          ? row('Perpetual license', money(opt.licenseOneTime))
          : row('Retain-access buy-in', money(opt.retainBuyIn))}
        {spOn && (result.switchPilot?.configLabor ?? 0) > 0 && row('SwitchPilot configuration', money(result.switchPilot?.configLabor ?? 0))}
        {spOn && spExtraRows}
        <Divider my={1} />
        {row('One-time total', money(oneTimeTotal), true)}
        {oneTimeTotal > 0 && (
          <Text fontSize="xx-small" color={sub}>{`50/50: ${money(deposit)} down · ${money(oneTimeTotal - deposit)} on completion`}</Text>
        )}
        {(spOn || !isPerpetual) && <Divider my={1} />}
        {!isPerpetual && row(spOn ? 'Monthly RNWY license' : 'Monthly license', `${money(opt.monthlyLicense)}/mo`, !spOn)}
        {spOn && row('Monthly SwitchPilot license', `${money(spMo)}/mo`)}
        {spOn && row('Total monthly license', `${money(opt.monthlyLicense + spMo)}/mo`, true)}
        {spOn && (
          <Box mt={2} borderWidth="1px" borderColor={flagAccent} borderRadius="md" bg={flagBg} overflow="hidden">
            <HStack spacing={0} align="stretch">
              <Box w="4px" bg={flagAccent} flexShrink={0} />
              <Box p={2} flex="1" minW={0}>
                <Badge colorScheme="purple" fontSize="0.62rem" textTransform="uppercase" letterSpacing="0.04em" mb={1}>
                  Subscription add-on
                </Badge>
                <Text fontSize="xx-small" color={flagText}>
                  SwitchPilot bills monthly — approving this option flags the deal for
                  subscription enrollment.
                </Text>
              </Box>
            </HStack>
          </Box>
        )}
        {/* item 652/656 — editable per-option FAQ / notes; prints on the PDF, seeded from Settings */}
        <Box mt={2} pt={2} borderTopWidth="1px" borderColor={border}>
          <Text fontSize="xx-small" color={sub} mb={1}>FAQ / notes for this option (prints on the proposal)</Text>
          <Textarea
            size="sm"
            minH="70px"
            fontSize="xs"
            value={pricing[key].faq ?? ''}
            aria-label={`${key} faq`}
            onChange={(e) => setFaq(key, e.target.value)}
          />
        </Box>
      </Box>
    );
  };

  const sp = result.switchPilot;
  const switchPilotCard = hasSwitchPilot && (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" bg={cardBg} p={3}>
      <Text fontWeight="bold" fontSize="sm">SwitchPilot module</Text>
      {/* item 647 — depict which banded license tier is active */}
      <Text fontSize="xx-small" color={sub} mb={2}>
        {`Subscription add-on — own contract.${sp.tier > 0 ? ` Tier ${sp.tier}.` : ''}`}
      </Text>
      {/* item 680 (Chad) — itemize the switch config + any catalog add-ons so you see where the one-time comes from */}
      {sp.configLabor > 0 && row('Switch configuration', money(sp.configLabor))}
      {spExtraRows}
      {sp.oneTimeConfig > 0 && <Divider my={1} />}
      {sp.oneTimeConfig > 0 && row('Configuration (one-time)', money(sp.oneTimeConfig), true)}
      <Divider my={1} />
      {row('Monthly subscription', `${money(sp.monthly)}/mo`, true)}
      {/* item 647 — SwitchPilot is subscription-only; the perpetual buy-out line was removed */}
    </Box>
  );

  return (
    <Box>
      <HStack mb={2}>
        <Text fontWeight="semibold" fontSize="sm">RNWY Licensing — three options (computed from Schedule B)</Text>
        {result.venueCount > 0 && (
          <Badge colorScheme="purple">{`${result.venueCount} venue${result.venueCount === 1 ? '' : 's'}`}</Badge>
        )}
        {result.isSwitchPilotOnly && <Badge colorScheme="orange">SwitchPilot only</Badge>}
        {loading && <Spinner size="xs" />}
      </HStack>
      {result.isSwitchPilotOnly ? (
        // SwitchPilot-only → no venue-driven license pricing; Perpetual is suppressed.
        <>
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={3}>{switchPilotCard}</SimpleGrid>
          {/* item 1395 — Help Desk rides the SwitchPilot subscription too (subscription tier rule:
              Platinum at Gold's rate, Titanium at its own; Gold itself not offered) */}
          <Box mt={2}>
            <Text fontSize="xs" color={sub}>
              {`Help Desk (optional add-on, monthly): Platinum ${money(result.helpDesk.gold)} · Titanium ${money(result.helpDesk.titanium)}`}
            </Text>
          </Box>
        </>
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={3}>
            {card('perpetual', 'Perpetual', 'Paid once, upfront.', result.perpetual, true)}
            {card('twoYear', '2-Year subscription', 'Monthly license, 2-yr term.', result.twoYear, false)}
            {card('fiveYear', '5-Year subscription', 'Monthly license, 5-yr term.', result.fiveYear, false)}
          </SimpleGrid>
          {switchPilotCard && (
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={3} mt={3}>{switchPilotCard}</SimpleGrid>
          )}
          <Box mt={2}>
            <Text fontSize="xs" color={sub}>
              {`Help Desk (optional add-on, monthly): Gold ${money(result.helpDesk.gold)} · Platinum ${money(result.helpDesk.platinum)} · Titanium ${money(result.helpDesk.titanium)}`}
            </Text>
            <Text fontSize="xx-small" color={sub}>Owner-supplied processors (hardware) are added as a manual line at contract.</Text>
          </Box>
        </>
      )}
      {/* item 1546 — the rep sees what rides with each option right where it is priced (never priced itself) */}
      <RnwySubscriptionEnhancementsNote compact />
    </Box>
  );
};

RnwyOptionsPreview.defaultProps = { crestron: false };

export default RnwyOptionsPreview;
